/**
 * Branding / Performans sınıflandırması — custom-report sayfalarının ORTAK kuralı.
 *
 * Grup kuralı: kampanya ADINDA "Branding" veya "Performans" geçer (Yataş Bedding
 * 117/117 Google + 31/31 Meta, EnzaHome ve puffy de aynı). Bu sinyal her zaman kazanır.
 *
 * Ama her ajans bu adlandırmayı kullanmıyor — Divanev'in 27 Google + 30 Meta
 * kampanyasının HİÇBİRİNDE bu kelimeler yok (`DH-[Reach]`, `DH-[Pmax]` … biçiminde).
 * Ada bakan kural orada hiçbir şey söyleyemediği için üç rapor aynı harcamayı üç
 * farklı kovaya koyuyordu. Ad sinyali yoksa platformun KENDİ alanına düşüyoruz:
 * Meta objective'i, Google kampanya tipi. Üst huni = branding.
 *
 * Anahtar kelimeli markalar 1. adımda karar aldığı için bu geri düşüş onları etkilemez.
 */

/**
 * Tüm harcaması branding sayılan markalar. Divanev'de site e-ticaret yapmıyor
 * (GA4'te `purchase` event'i hiç yok, gelir her ay 0, bağlı e-ticaret platformu da yok),
 * dolayısıyla performans/branding ayrımının karşılığı yok — mecra fark etmeksizin
 * her kampanya branding kovasına gider.
 */
export const BRANDING_ONLY_BRAND_IDS = new Set([
  'dfcf54dd-b00d-434a-be1e-27b6484848eb', // Divanev
]);
export const isBrandingOnlyBrand = (brandId) => BRANDING_ONLY_BRAND_IDS.has(brandId);

/** Ad içindeki açık sinyal. İkisi de varsa karar veremez → null. */
export function explicitNameBucket(name) {
  const n = (name || '').toString().toLowerCase();
  const hasBranding = n.includes('branding');
  const hasPerformance = n.includes('performans') || n.includes('performance');
  if (hasBranding && !hasPerformance) return 'branding';
  if (hasPerformance && !hasBranding) return 'performance';
  return null;
}

/** Adı üst huni kanalını işaret eden kampanyalar (ad sinyali yokken bakılır). */
const BRANDING_NAME_KEYWORDS = ['branding', 'demand gen', 'display', 'youtube'];
export const nameIsBranding = (name) =>
  BRANDING_NAME_KEYWORDS.some((k) => (name || '').toString().toLowerCase().includes(k));

/** Google kampanya tipi → üst huni mi? `AdvertisingChannelType.DEMAND_GEN` gibi
 *  ENUM biçimleri de gelir; alt çizgi/boşluk farkına takılmamak için normalize edilir. */
export function googleTypeIsBranding(campaignType) {
  const t = (campaignType || '').toString().toLowerCase().replace(/[_.]/g, ' ');
  if (!t) return false;
  return (
    t.includes('demand gen') ||
    t.includes('display') ||
    t.includes('youtube') ||
    t.includes('video')
  );
}

/** Meta objective → üst huni mi? (OUTCOME_TRAFFIC / OUTCOME_SALES / LEADS = performans) */
const META_BRANDING_OBJECTIVES = new Set([
  'REACH', 'AWARENESS', 'BRAND_AWARENESS', 'OUTCOME_AWARENESS',
  'ENGAGEMENT', 'POST_ENGAGEMENT', 'OUTCOME_ENGAGEMENT',
  'VIDEO_VIEWS', 'THRUPLAY', 'STORE_VISITS', 'LOCAL_AWARENESS',
]);
export function metaObjectiveIsBranding(objective) {
  const o = (objective || '').toString().toUpperCase().trim();
  return o ? META_BRANDING_OBJECTIVES.has(o) : false;
}

/** TikTok ad/objective anahtarları — TikTok kampanyaları Reach/Engagement diye adlandırılır. */
const TIKTOK_BRANDING_KEYWORDS = [
  'brand', 'reach', 'video view', 'videoview', 'engagement', 'awareness',
  'consideration', 'community', 'tanıtım', 'tanitim', 'promote', 'bilinirlik',
  'izlenme', 'erişim', 'erisim', 'etkileşim', 'etkilesim', 'reels',
];
export function tiktokIsBranding(name, objective) {
  const s = `${name || ''} ${objective || ''}`.toLowerCase();
  return TIKTOK_BRANDING_KEYWORDS.some((k) => s.includes(k));
}

/** Pinterest objective_type → üst huni mi? (WEB_CONVERSION / CATALOG_SALES / SHOPPING = performans) */
const PINTEREST_BRANDING_OBJECTIVES = new Set([
  'AWARENESS', 'BRAND_AWARENESS', 'VIDEO_VIEW', 'CONSIDERATION', 'ENGAGEMENT',
]);
export function pinterestIsBranding(name, objective) {
  const o = (objective || '').toString().toUpperCase().trim();
  if (o) return PINTEREST_BRANDING_OBJECTIVES.has(o);
  return nameIsBranding(name);
}

/**
 * Tek karar noktası.
 * @param {object} p
 * @param {string} p.name          kampanya adı
 * @param {'google'|'meta'|'tiktok'|'pinterest'|'rtbhouse'|string} [p.platform]
 * @param {string} [p.campaignType] Google kampanya tipi
 * @param {string} [p.objective]    Meta/TikTok objective
 * @param {string} [p.brandId]      marka — hepsi-branding markalarında her şey branding
 * @returns {'branding'|'performance'}
 */
export function classifyCampaign({ name, platform, campaignType, objective, brandId } = {}) {
  if (brandId && isBrandingOnlyBrand(brandId)) return 'branding';

  const explicit = explicitNameBucket(name);
  if (explicit) return explicit;

  const p = (platform || '').toString().toLowerCase();
  if (p.includes('tiktok')) return tiktokIsBranding(name, objective) ? 'branding' : 'performance';
  if (p.includes('pinterest')) return pinterestIsBranding(name, objective) ? 'branding' : 'performance';
  if (p.includes('meta') || p.includes('facebook')) {
    if (metaObjectiveIsBranding(objective)) return 'branding';
    // objective gelmiyorsa ada bakan eski davranışı koru
    return objective ? 'performance' : (nameIsBranding(name) ? 'branding' : 'performance');
  }
  // RTB House: retargeting DSP'si. Ad açık sinyal taşımıyorsa (Chakra
  // `Main subcampaign`, EnzaHome `TR_enzahome`) performance'a düşer. Google'ın
  // kanal sözlüğüne BAKMA: RTB House envanterinin tamamı display, oradaki
  // "display" kelimesi hunide üst demek değil.
  if (p.includes('rtb')) return 'performance';
  // Google ve tanımsız platformlar: ad + kampanya tipi
  return nameIsBranding(name) || googleTypeIsBranding(campaignType) ? 'branding' : 'performance';
}

export const isBrandingCampaign = (p) => classifyCampaign(p) === 'branding';
